import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { DepreciationPoint } from '../types';

interface D3DepreciationChartProps {
    data: DepreciationPoint[];
    width?: number;
    height?: number;
}

const D3DepreciationChart: React.FC<D3DepreciationChartProps> = ({
    data,
    width = 800,
    height = 300
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const gradientIdRef = useRef<string>(`depreciation-gradient-${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        if (!svgRef.current || data.length === 0) return;

        // Clear previous content
        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current);
        const margin = { top: 20, right: 40, bottom: 50, left: 80 };
        const chartWidth = width - margin.left - margin.right;
        const chartHeight = height - margin.top - margin.bottom;

        // Create the main group
        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Parse dates
        const dataParsed = data
            .map(d => {
                const parsedDate = d3.isoParse(d.date);
                return parsedDate ? { date: parsedDate, value: d.value } : null;
            })
            .filter((d): d is { date: Date; value: number } => d !== null);

        if (dataParsed.length === 0) return;

        // Set up scales
        const dateExtent = d3.extent(dataParsed, d => d.date) as [Date, Date];

        if (!dateExtent[0] || !dateExtent[1]) return;

        const xScale = d3.scaleTime()
            .domain(dateExtent)
            .range([0, chartWidth]);

        const yScale = d3.scaleLinear()
            .domain([0, d3.max(dataParsed, d => d.value)! * 1.05] as [number, number])
            .range([chartHeight, 0]);

        // Create line generator
        const line = d3.line<{ date: Date; value: number }>()
            .x(d => xScale(d.date))
            .y(d => yScale(d.value))
            .curve(d3.curveMonotoneX);

        // Add area under the curve
        const area = d3.area<{ date: Date; value: number }>()
            .x(d => xScale(d.date))
            .y0(chartHeight)
            .y1(d => yScale(d.value))
            .curve(d3.curveMonotoneX);

        // Add gradient for area fill (with unique ID)
        const gradient = svg.append('defs')
            .append('linearGradient')
            .attr('id', gradientIdRef.current)
            .attr('x1', '0%')
            .attr('y1', '0%')
            .attr('x2', '0%')
            .attr('y2', '100%');

        gradient.append('stop')
            .attr('offset', '0%')
            .attr('stop-color', '#3b82f6')
            .attr('stop-opacity', 0.3);

        gradient.append('stop')
            .attr('offset', '100%')
            .attr('stop-color', '#3b82f6')
            .attr('stop-opacity', 0.05);

        // Add area
        g.append('path')
            .datum(dataParsed)
            .attr('fill', `url(#${gradientIdRef.current})`)
            .attr('d', area);

        // Add X axis
        const timeFormat = d3.timeFormat('%b %Y');
        const xAxis = g.append('g')
            .attr('transform', `translate(0,${chartHeight})`)
            .call(d3.axisBottom(xScale)
                .ticks(d3.timeMonth.every(3) as any)
                .tickFormat((d) => timeFormat(d as Date)));

        xAxis.selectAll('text')
            .style('text-anchor', 'end')
            .attr('dx', '-.8em')
            .attr('dy', '.15em')
            .attr('transform', 'rotate(-45)');

        xAxis.append('text')
            .attr('fill', '#6b7280')
            .attr('x', chartWidth / 2)
            .attr('y', 45)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('font-weight', '500')
            .text('Date');

        // Add Y axis
        const yAxis = g.append('g')
            .call(d3.axisLeft(yScale)
                .tickFormat(d => {
                    const value = d as number;
                    if (value >= 1000000) {
                        return `$${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                        return `$${(value / 1000).toFixed(0)}K`;
                    }
                    return `$${value.toFixed(0)}`;
                }));

        yAxis.append('text')
            .attr('fill', '#6b7280')
            .attr('transform', 'rotate(-90)')
            .attr('y', -60)
            .attr('x', -chartHeight / 2)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .style('font-weight', '500')
            .text('Value');

        // Add grid lines
        g.append('g')
            .attr('class', 'grid')
            .attr('transform', `translate(0,${chartHeight})`)
            .call(d3.axisBottom(xScale)
                .ticks(d3.timeMonth.every(3) as any)
                .tickSize(-chartHeight)
                .tickFormat(() => ''))
            .style('stroke-dasharray', '3,3')
            .style('opacity', 0.2)
            .style('stroke', '#9ca3af');

        g.append('g')
            .attr('class', 'grid')
            .call(d3.axisLeft(yScale)
                .tickSize(-chartWidth)
                .tickFormat(() => ''))
            .style('stroke-dasharray', '3,3')
            .style('opacity', 0.2)
            .style('stroke', '#9ca3af');

        // Add the line
        g.append('path')
            .datum(dataParsed)
            .attr('fill', 'none')
            .attr('stroke', '#3b82f6')
            .attr('stroke-width', 3)
            .attr('d', line);

        // Add data points
        g.selectAll('.dot')
            .data(dataParsed)
            .enter()
            .append('circle')
            .attr('class', 'dot')
            .attr('cx', d => xScale(d.date))
            .attr('cy', d => yScale(d.value))
            .attr('r', 4)
            .attr('fill', '#3b82f6')
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .style('cursor', 'pointer');

        // Add tooltip
        const tooltip = d3.select('body')
            .append('div')
            .style('position', 'absolute')
            .style('background', 'rgba(0, 0, 0, 0.85)')
            .style('color', 'white')
            .style('padding', '10px 12px')
            .style('border-radius', '6px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('font-size', '12px')
            .style('box-shadow', '0 4px 6px rgba(0, 0, 0, 0.1)')
            .style('z-index', '1000');

        g.selectAll('.dot')
            .on('mouseover', function (event, d) {
                const point = d as { date: Date; value: number };
                const date = point.date;
                const value = point.value;
                const formattedValue = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                }).format(value);

                return tooltip
                    .style('opacity', 1)
                    .html(`<strong>${d3.timeFormat('%b %d, %Y')(date)}</strong><br/>Value: ${formattedValue}`);
            })
            .on('mousemove', function (event) {
                tooltip
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY - 10) + 'px');
            })
            .on('mouseout', function () {
                tooltip.style('opacity', 0);
            });

        // Cleanup tooltip on unmount
        return () => {
            tooltip.remove();
        };
    }, [data, width, height]);

    if (data.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center">
                    <p className="text-gray-500 text-sm">No depreciation data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full overflow-x-auto">
            <svg ref={svgRef} width={width} height={height} style={{ display: 'block' }}></svg>
        </div>
    );
};

export default D3DepreciationChart;

